import { useState, useEffect } from 'react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  Typography,
  TextField,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useField } from 'formik';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useFocusOnError } from '../../shared/hooks';
import SelectList from './components/SelectList';
import getStyles from './styles';
import './style.css';

const initialsNoCase = ['CPF', 'CEP', 'DDD', 'Código de área - DDI', 'DDI'];

const changeCaseFirstLetter = (params: any) => {
  if (typeof params === 'string' && !initialsNoCase.includes(params)) {
    return params[0]?.toUpperCase() + params?.slice(1)?.toLowerCase();
  }
  return params;
};

type Options = {
  label: string;
  value: string;
};

type Props = {
  name: string;
  options: Options[];
  placeholder?: string;
  startsWithNone?: boolean;
  inputProps?: any;
  value?: any;
  label: string;
  disabled?: boolean;
  isMulti?: boolean;
  showLabel?: boolean;
  showPlaceholder?: boolean;
  noCase?: boolean;
  labelGrey?: boolean;
  labelMargin?: boolean;
  noError?: boolean;
};

const InvisibleButton = styled('button')({
  display: 'none',
});

const AutoComplete = ({
  name,
  options,
  label,
  disabled,
  isMulti,
  showLabel = true,
  showPlaceholder = false,
  labelGrey = false,
  placeholder = '',
  noError = false,
  startsWithNone,
  inputProps,
  value: _value,
  noCase,
  labelMargin,
  ...props
}: Props) => {
  const theme = useTheme();
  const classes: any = getStyles(theme);
  const [field, meta, fieldProps] = useField(name);
  const [valueKey, setValueKey] = useState<any>(field.value ? field.value : '');
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    const setPreviousSelected = () => {
      const [selected] = options.filter((option) => {
        return option.value === field.value;
      });
      setValueKey(selected?.value);
    };

    if (field.value && options) setPreviousSelected();
  }, [options]);

  const handleChange = (event: any) => {
    const { value: changeValue } = event.target;

    if (changeValue && isMulti) {
      fieldProps.setValue(changeValue);
    } else if (changeValue) {
      fieldProps.setValue(changeValue);
      setValueKey(changeValue);
    }
  };

  useEffect(() => {
    if (options?.length === 1 && !field.value) {
      queueMicrotask(() => {
        handleChange({ target: { value: options[0].value } });
      });
    }
  }, [options]);

  useEffect(() => {
    if (isMulti && valueKey?.length > 0) {
      queueMicrotask(() => {
        handleChange({ target: { value: valueKey } });
      });
    }
  }, [isMulti, valueKey]);

  useFocusOnError({ fieldName: name, fieldId: `form-field-${name}` });

  return (
    <FormControl fullWidth error={!!meta.error} sx={classes.select}>
      <InvisibleButton
        onClick={() =>
          handleChange({
            target: { value: isMulti ? [options[0].value] : options[0].value },
          })
        }
        data-testid={`${name}-invisible`}
      />
      {showLabel && !isMulti && (
        <Typography
          sx={{ ...classes.title, ...(labelGrey ? classes.titleColor : {}) }}
        >
          {changeCaseFirstLetter(label)}
        </Typography>
      )}
      {!isMulti && (
        <Autocomplete
          {...props}
          disableClearable
          openOnFocus
          disabled={disabled}
          options={options || []}
          id={`form-field-${name}`}
          value={
            (options?.filter(
              (el: any) => el.value === field?.value
            )[0] as any) || ''
          }
          inputValue={inputValue}
          getOptionLabel={(option: any) => option?.label || ''}
          onChange={(_event: any, newValue: any) => {
            fieldProps.setValue(newValue.value);
            setValueKey(newValue);
          }}
          onInputChange={(_event: any, newInputValue: any) => {
            setInputValue(newInputValue);
          }}
          isOptionEqualToValue={(option: any, valueSelected: any) =>
            valueSelected.value === ''
              ? valueSelected?.value === option.value
              : true
          }
          forcePopupIcon
          popupIcon={<KeyboardArrowDownIcon />}
          slotProps={{
            listbox: { sx: classes.selectMenu },
            popupIndicator: { sx: classes.selectIcon },
          }}
          sx={{
            ...classes.selectRoot,
            '& .MuiOutlinedInput-root': {
              padding: '0 !important',
            },
            '& .MuiOutlinedInput-input': {
              ...classes.inputOutlined,
            },
            ...(meta.touched && meta.error
              ? { border: '1px solid #f44336' }
              : {}),
          }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              placeholder={showPlaceholder ? placeholder : ''}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'off',
              }}
              variant="outlined"
            />
          )}
        />
      )}
      {isMulti && (
        <SelectList
          className=""
          options={options}
          title={changeCaseFirstLetter(label)}
          value={valueKey}
          setValue={setValueKey}
          name={name}
        />
      )}

      {meta.touched && meta?.error && !noError && (
        <FormHelperText>{meta.error}</FormHelperText>
      )}
    </FormControl>
  );
};

export default AutoComplete;
